import React, { useCallback, useReducer, useMemo, useRef } from "react";
import type { FC } from "react";

import { Box, Text, VStack, useToast, Center, Spinner, Button } from "native-base";
import { useMutation } from "react-relay";

import SideBar from "pianofunclub-crm/components/Drawers/SideBar";
import type {
  ProfileStackNavigatorProps,
  ProfileStackRouteProps,
} from "pianofunclub-crm/navigation/ProfileNavigator";

import TextInput from "pianofunclub-shared/components/Inputs/TextInput";
import type { TextInputRef } from "pianofunclub-shared/components/Inputs/TextInput";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import type {
  EditMyProfileMutation,
  EditMyProfileMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/EditMyProfileMutation.graphql";
import { edit_my_profile } from "pianofunclub-shared/relay/graphql/accounts/EditMyProfile";
import { createFormReducer } from "pianofunclub-shared/utils/reducers";

type NavigationProps = ProfileStackNavigatorProps<"ChangeName">;

type RouteProps = ProfileStackRouteProps<"ChangeName">;

interface ScreenProps {
  navigation: NavigationProps;
  route: RouteProps;
}

type FormValues = {
  firstName: string;
  lastName: string;
};

const SIDE_BAR_WIDTH = 12;

const ChangeNameScreen: FC<ScreenProps> = (props) => {
  const { navigation } = props;

  const { user } = useAuth();

  const initialState = useMemo(() => {
    return {
      formIsEdited: false,
      formIsValid: true,
      inputValidities: {
        firstName: true,
        lastName: true,
      },
      inputValues: {
        firstName: user?.firstName ?? "",
        lastName: user?.lastName ?? "",
      },
    };
  }, [user?.firstName, user?.lastName]);

  const formReducer = createFormReducer<FormValues>(initialState);
  const [formState, dispatchFormState] = useReducer(formReducer, initialState);

  const updateToast = useToast();
  const updateToastId = "updateToast";
  const lastNameInputRef = useRef<TextInputRef>(null);

  const [commitEditMyProfile, editMyProfileInFlight] =
    useMutation<EditMyProfileMutation>(edit_my_profile);

  const editMyProfile = useCallback(
    (variables: { firstName: string; lastName: string }) => {
      const editMyProfileConfig = {
        variables: {
          input: {
            firstName: variables.firstName,
            lastName: variables.lastName,
          },
        },
        onCompleted: (response: EditMyProfileMutation$data) => {
          if (response?.editMyProfile?.success) {
            if (!updateToast.isActive(updateToastId)) {
              updateToast.show({
                id: updateToastId,
                render: ({ id }: { id: string }) => (
                  <ToastAlert id={id} status="success" title={"Name updated"} toast={updateToast} />
                ),
              });
            }
            navigation.goBack();
          } else if (!updateToast.isActive(updateToastId)) {
            updateToast.show({
              id: updateToastId,
              render: ({ id }: { id: string }) => (
                <ToastAlert
                  description={"Please get in touch with one of the team"}
                  id={id}
                  status="error"
                  title={"Name could not be updated"}
                  toast={updateToast}
                />
              ),
            });
          }
        },
      };

      commitEditMyProfile(editMyProfileConfig);
    },
    [commitEditMyProfile, navigation, updateToast]
  );

  const inputChangeHandler = useCallback(
    (inputIdentifier?: string | number, inputValue?: string, isValid?: boolean) => {
      if (typeof inputIdentifier === "string" && typeof inputValue === "string") {
        dispatchFormState({
          input: inputIdentifier,
          isValid: isValid,
          value: inputValue,
        });
      }
    },
    [dispatchFormState]
  );

  const inputSubmitHandler = useCallback(
    (inputIdentifier?: string | number) => {
      switch (inputIdentifier) {
        case "firstName":
          lastNameInputRef.current?.focus();
          break;
        case "lastName":
          if (formState.formIsValid && !editMyProfileInFlight) {
            editMyProfile({
              firstName: formState.inputValues.firstName,
              lastName: formState.inputValues.lastName,
            });
          }
      }
    },
    [
      formState.formIsValid,
      formState.inputValues.firstName,
      formState.inputValues.lastName,
      editMyProfile,
      editMyProfileInFlight,
    ]
  );

  return (
    <Box flex={1}>
      <SideBar isCollapsed navigation={navigation} width={SIDE_BAR_WIDTH} />
      <Center bg="surface.100" flex={1} ml={SIDE_BAR_WIDTH} pt="70">
        <VStack alignItems="center" space="4">
          <Text fontSize="2xl" fontWeight={700} mb="4">
            Change Name
          </Text>
          <TextInput
            autoCapitalize="words"
            centerLabel
            errorMessage="Please enter a first name."
            id="firstName"
            initiallyValid={true}
            initialValue={user?.firstName}
            invalidIndicator
            keyboardType="default"
            label="First Name"
            labelStyle={{ color: "surface.700" }}
            maxLength={30}
            onInputChange={inputChangeHandler}
            onSubmit={inputSubmitHandler}
            placeholder="Enter your first name"
            size="xl"
            textAlign="center"
          />
          <TextInput
            ref={lastNameInputRef}
            autoCapitalize="words"
            centerLabel
            errorMessage="Please enter a last name."
            id="lastName"
            initiallyValid={true}
            initialValue={user?.lastName}
            invalidIndicator
            keyboardType="default"
            label="Last Name"
            labelStyle={{ color: "surface.700" }}
            maxLength={30}
            mb="4"
            onInputChange={inputChangeHandler}
            onSubmit={inputSubmitHandler}
            placeholder="Enter your last name"
            size="xl"
            textAlign="center"
          />
          <Button
            colorScheme="primary"
            height="60px"
            isDisabled={!formState.formIsValid || editMyProfileInFlight}
            onPress={() => {
              editMyProfile({
                firstName: formState.inputValues.firstName,
                lastName: formState.inputValues.lastName,
              });
            }}
            width="200px"
          >
            {editMyProfileInFlight ? (
              <Spinner color="white" />
            ) : (
              <Text color="white" fontSize="xl">
                Save
              </Text>
            )}
          </Button>
          <Button
            _hover={{ bg: "transparent", opacity: 0.8 }}
            _pressed={{ bg: "transparent", opacity: 0.7 }}
            colorScheme="surface"
            mt="4"
            onPress={() => navigation.goBack()}
            variant="ghost"
          >
            Go Back
          </Button>
        </VStack>
      </Center>
    </Box>
  );
};

export const screenOptions = {
  headerTitle: "Change Name",
};

export default React.memo(ChangeNameScreen);
